import {
  BrowseLearningOpportunities,
  CompetenceBaseEx,
} from "@/server/backend-api";
import {
  Audience2,
  LearningOpportunityStatus,
  LearningOpportunityType,
  StudyFormat,
  Tag,
} from "@/server/backend-api/generated";
import { OrganisationBase } from "@/server/backend-api/organisation";
import { addOrRemove } from "@/utils/array";
import { mcn } from "../utils";
import Checkbox from "./Input/Checkbox";
import { studyFormatToString } from "@/server/backend-api/enumToText";
import { PropsWithChildren, useEffect, useState } from "react";
import {
  IconActionCalendar,
  IconActionChevronDown,
  IconActionFilter,
} from "@/components/Icons";
import { sortTopicsInFilterTopic } from "@/utils/competenceTree";
import { LearningOpportunityFilterState } from "@/utils/browseFilter";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export type ValueWithDisplayName<T> = {
  value: T;
  displayName: string;
};

export type LearningOppTypesDisplay =
  ValueWithDisplayName<LearningOpportunityType> & {
    className?: string;
  };

export const filterLearningOppsByStatePredicate = (
  learningOpportunity: BrowseLearningOpportunities,
  args: LearningOpportunityFilterState
): boolean => {
  const loTopicLevelIds = learningOpportunity.topicsWithLevel?.map(
    (topic) => topic
  );
  if (args.selectedLearningOppTypes.length !== 0) {
    if (!args.selectedLearningOppTypes.includes(learningOpportunity.type)) {
      return false;
    }
  }

  if (
    args.selectedTopicIds.length !== 0 &&
    args.selectedLevelIds.length !== 0
  ) {
    const loTopicLevelIds = learningOpportunity.topicsWithLevel?.map(
      (topic) => topic
    );
    const oneOfLOTopicWithLevelIsSelected = loTopicLevelIds?.some(
      (topiclevel) =>
        args.selectedTopicIds.includes(topiclevel.topicId) &&
        args.selectedLevelIds?.includes(
          topiclevel.levelId === null ? "" : topiclevel.levelId.toString()
        )
    );

    if (!oneOfLOTopicWithLevelIsSelected) {
      return false;
    }
  }
  if (
    args.selectedTopicIds.length !== 0 &&
    args.selectedLevelIds.length === 0
  ) {
    const loTopicIds = learningOpportunity.topics.map((topic) => topic.id);
    // LOs lacking competences will always be removed if any topics are selected.
    const oneOfLOTopicIdsIsSelected = loTopicIds.some((id) =>
      args.selectedTopicIds.includes(id)
    );
    if (!oneOfLOTopicIdsIsSelected) {
      const oneOfLOSubTopicIsSelected = loTopicLevelIds?.some((subTopic) =>
        args.selectedTopicIds.includes(subTopic.topicId)
      );
      if (!oneOfLOSubTopicIsSelected) {
        return false;
      }
    }
  }
  if (
    args.selectedLevelIds.length !== 0 &&
    args.selectedTopicIds.length === 0
  ) {
    const loLevelIds = learningOpportunity.topicsWithLevel?.map(
      (level) => level
    );
    const oneOfLOLevelIsSelected = loLevelIds?.some((level) =>
      args.selectedLevelIds?.includes(
        level.levelId === null ? "" : level.levelId.toString()
      )
    );
    if (!oneOfLOLevelIsSelected) {
      return false;
    }
  }
  if (args.selectedTagIds.length !== 0) {
    const loTagIds = learningOpportunity.tags?.map((tag) => tag.id);
    const oneOfLOTagIdsIsSelected = loTagIds?.some((id) =>
      args.selectedTagIds.includes(id)
    );
    if (!oneOfLOTagIdsIsSelected) {
      return false;
    }
  }

  if (args.selectedUniversityIds.length !== 0) {
    const universityName = learningOpportunity.hostingUniversityId;
    // Opps lacking a hosting university will always be removed if any topics are selected.
    if (universityName === null) {
      return false;
    }
    if (!args.selectedUniversityIds.includes(universityName)) {
      return false;
    }
  }
  if (args.selectedAudienceIds.length !== 0) {
    const loAudienceIdsRaw = learningOpportunity.audienceIds;

    const loAudienceIds = Array.isArray(loAudienceIdsRaw)
      ? loAudienceIdsRaw
      : loAudienceIdsRaw
      ? [loAudienceIdsRaw]
      : [];

    const hasMatchingAudience = loAudienceIds.some((audienceId) =>
      args.selectedAudienceIds.includes(audienceId)
    );

    if (!hasMatchingAudience) {
      return false;
    }
  }
  if (args.selectedPaces.length !== 0) {
    if (args.selectedPaces.includes("Self-paced")) {
      if (!learningOpportunity.selfPaced) {
        return false;
      }
    }
  }
  if (args.selectedCredits.length !== 0) {
    const hasVol = args.selectedCredits.includes("Volume of Learning");
    const hasEcts = args.selectedCredits.includes("ECTS");

    if (hasVol && hasEcts) {
      if (
        ((learningOpportunity.ects === null ||
          learningOpportunity.ects === 0) &&
          learningOpportunity.volumeOfLearning === null) ||
        learningOpportunity.volumeOfLearning === 0
      ) {
        return false;
      }
    } else if (hasVol) {
      if (
        learningOpportunity.volumeOfLearning === null ||
        learningOpportunity.volumeOfLearning === 0
      ) {
        return false;
      }
    } else if (hasEcts) {
      if (learningOpportunity.ects === null || learningOpportunity.ects === 0) {
        return false;
      }
    }
  }

  if (args.selectedLearningOppStatuses.length !== 0) {
    if (
      learningOpportunity.status === null ||
      !args.selectedLearningOppStatuses.includes(learningOpportunity.status)
    ) {
      return false;
    }
  }

  if (args.selectedStudyFormats.length !== 0) {
    if (
      learningOpportunity.studyFormat === null ||
      !args.selectedStudyFormats.includes(learningOpportunity.studyFormat)
    ) {
      return false;
    }
  }
  if (
    args.selectedStudyPeriodsFrom.length !== 0 ||
    args.selectedStudyPeriodsTo.length !== 0
  ) {
    const startDate = new Date(
      learningOpportunity.startDate ? learningOpportunity.startDate : ""
    );
    const startDateFrom = new Date(args.selectedStudyPeriodsFrom[0]);
    startDateFrom.setHours(0, 0, 0, 0);
    const startDateTo = new Date(args.selectedStudyPeriodsTo[0]);
    startDateTo.setHours(23, 59, 59, 999);
    if (args.selectedStudyPeriodsTo.length === 0) {
      if (args.selectedStudyPeriodsFrom.length > 0) {
        return startDate >= startDateFrom;
      }
      return true;
    } else if (args.selectedStudyPeriodsFrom.length === 0) {
      if (args.selectedStudyPeriodsTo.length > 0) {
        return startDate <= startDateTo;
      }
      return true;
    } else {
      return startDate >= startDateFrom && startDate <= startDateTo;
    }
  }
  return true;
};

function FilterSection(
  props: PropsWithChildren<{
    title: string;
    isExpanded: boolean;
    onExpandClicked?: (isExpanded: boolean) => void;
  }>
) {
  const { title, isExpanded, onExpandClicked } = props;
  return (
    <div>
      <button
        className="w-full flex items-center"
        onClick={() => {
          if (onExpandClicked) {
            onExpandClicked(!isExpanded);
          }
        }}
      >
        <IconActionChevronDown
          className={mcn(
            "w-4 h-4 mr-2",
            isExpanded ? "rotate-180" : "rotate-0"
          )}
        />
        <h2 className="text-style-subtitle2 text-light-yellow">{title}</h2>
      </button>
      {isExpanded && (
        <div
          className="overflow-auto max-h-[15rem] [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:rounded-full
  [&::-webkit-scrollbar-track]:bg-grey-120
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-grey-40
  dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500"
        >
          {props.children}
        </div>
      )}
    </div>
  );
}

type Props = {
  show: boolean;
  topics: CompetenceBaseEx[];
  memberUniversities: OrganisationBase[];
  learningOppStatuses: ValueWithDisplayName<LearningOpportunityStatus>[];
  learningOppTypes: LearningOppTypesDisplay[];
  value: LearningOpportunityFilterState;
  onFilterStateChange(filterState: LearningOpportunityFilterState): void;
  containerClassName?: string;
  tags?: Tag[];
  audiences: Audience2[];
};

export const BrowseFilter = ({
  show,
  topics,
  memberUniversities,
  learningOppStatuses,
  learningOppTypes,
  tags,
  value,
  onFilterStateChange,
  containerClassName,
  audiences,
}: Props) => {
  const triggerOnChange = (newState: LearningOpportunityFilterState) => {
    onFilterStateChange({
      ...newState,
    });
  };
  const {
    selectedLearningOppTypes,
    selectedTopicIds,
    selectedUniversityIds,
    selectedLearningOppStatuses,
    selectedStudyFormats,
    selectedTagIds,
    selectedPaces,
    selectedCredits,
    selectedStudyPeriodsFrom,
    selectedStudyPeriodsTo,
    selectedAudienceIds,
  } = value;
  const topicsBySpecialTopicOrder = sortTopicsInFilterTopic(topics);
  const selectedUniversities = memberUniversities.filter((memberUni) =>
    selectedUniversityIds.includes(memberUni.id)
  );

  const [expansions, setExpansions] = useState({
    isLearningOppExpanded: false,
    isTopicsExpanded: false,
    isStatusExpanded: true,
    isStudyFormatsExpanded: false,
    isUniversitiesExpanded: false,
    isTagsExpanded: false,
    isStudyPeriodExpanded: false,
    isPaceExpanded: false,
    isCreditsExpanded: false,
    isLearnerTypeExpanded: false,
  });
  const [startDate, setStartDate] = useState<string>("");
  const [secondStartDate, setSecondStartDate] = useState<string>("");
  function triggerExpansionChange(section: keyof typeof expansions) {
    setExpansions((prevExpansions) => ({
      ...prevExpansions,
      [section]: !prevExpansions[section],
    }));
  }

  useEffect(() => {
    if (startDate !== "") {
      const formattedStartDate = format(new Date(startDate), "dd MMMM yyyy");
      const updatedSelectedStudyPeriodsFrom = [...selectedStudyPeriodsFrom];
      if (updatedSelectedStudyPeriodsFrom.length > 0) {
        updatedSelectedStudyPeriodsFrom[0] = formattedStartDate;
      } else {
        updatedSelectedStudyPeriodsFrom.push(formattedStartDate);
      }
      triggerOnChange({
        ...value,
        selectedStudyPeriodsFrom: updatedSelectedStudyPeriodsFrom,
      });
    }
  }, [startDate]);

  useEffect(() => {
    if (secondStartDate !== "") {
      const formattedSecondStartDate = format(
        new Date(secondStartDate),
        "dd MMMM yyyy"
      );
      const updatedSelectedStudyPeriodsTo = [...selectedStudyPeriodsTo];
      if (updatedSelectedStudyPeriodsTo.length > 0) {
        updatedSelectedStudyPeriodsTo[0] = formattedSecondStartDate;
      } else {
        updatedSelectedStudyPeriodsTo.push(formattedSecondStartDate);
      }
      triggerOnChange({
        ...value,
        selectedStudyPeriodsTo: updatedSelectedStudyPeriodsTo,
      });
    }
  }, [secondStartDate]);
  useEffect(() => {
    const resetDateIfEmpty = (
      selectedStudyPeriods: string[],
      date: string,
      setDate: React.Dispatch<React.SetStateAction<string>>
    ) => {
      if (selectedStudyPeriods.length === 0 && date !== "") {
        setDate("");
      }
    };
    resetDateIfEmpty(selectedStudyPeriodsFrom, startDate, setStartDate);
    resetDateIfEmpty(
      selectedStudyPeriodsTo,
      secondStartDate,
      setSecondStartDate
    );
  }, [selectedStudyPeriodsFrom, selectedStudyPeriodsTo]);
  //add more paces here if needed in the future or get it from the backend, right now since we only have one pace, we are hardcoding it
  const paces = ["Self-paced"];
  const credits = ["ECTS", "Volume of Learning"];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const studyPeriodsFrom = urlParams.get("studyPeriodsFrom");
    const studyPeriodsTo = urlParams.get("studyPeriodsTo");

    if (studyPeriodsFrom) {
      setStartDate(studyPeriodsFrom);
    }
    if (studyPeriodsTo) {
      setSecondStartDate(studyPeriodsTo);
    }
  }, []);

  const CustomInput = ({
    value,
    onClick,
  }: {
    value: string;
    onClick: React.MouseEventHandler<HTMLInputElement>;
  }) => {
    return (
      <div className="relative w-full">
        <input
          type="text"
          value={value}
          onClick={onClick}
          readOnly
          placeholder="MM/DD/YYYY"
          className="text-white bg-light-blue border-grey-40 border-[1px] rounded-1 py-2 pl-2 w-full focus-within:input-focus"
        />
        <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <IconActionCalendar className="w-5 h-5 text-grey-40" />
        </span>
      </div>
    );
  };

  return (
    <div className={mcn(containerClassName, !show && "hidden tablet-lg:block")}>
      <div className="bg-light-blue px-4 py-8 flex flex-col gap-y-8">
        <div className="flex gap-x-2 items-center">
          <IconActionFilter className="w-8 h-8" />
          <h3 className="text-style-headline4">Filter</h3>
        </div>
        <FilterSection
          title="STATUS"
          isExpanded={expansions.isStatusExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isStatusExpanded");
          }}
        >
          {learningOppStatuses.map((learningOppStatus, index) => (
            <Checkbox
              key={index}
              className="mt-sm rounded-3"
              id={`learning-opp-status-${learningOppStatus.value}`}
              checked={selectedLearningOppStatuses.includes(
                learningOppStatus.value
              )}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                triggerOnChange({
                  ...value,
                  selectedLearningOppStatuses: addOrRemove(
                    selectedLearningOppStatuses,
                    learningOppStatus.value,
                    checked
                  ),
                });
              }}
              labelProps={{
                text: learningOppStatus.displayName,
              }}
            />
          ))}
        </FilterSection>
        <FilterSection
          title="LEARNING OPPORTUNITY"
          isExpanded={expansions.isLearningOppExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isLearningOppExpanded");
          }}
        >
          {learningOppTypes.map((learningOppType, index) => (
            <div
              key={index}
              className={mcn("bg-blue mt-sm", learningOppType.className)}
            >
              <Checkbox
                id={`learning-opp-type-${learningOppType.value}`}
                checked={selectedLearningOppTypes.includes(
                  learningOppType.value
                )}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  triggerOnChange({
                    ...value,
                    selectedLearningOppTypes: addOrRemove(
                      selectedLearningOppTypes,
                      learningOppType.value,
                      checked
                    ),
                  });
                }}
                labelProps={{
                  text: learningOppType.displayName,
                }}
                className="ml-2xs"
              />
            </div>
          ))}
        </FilterSection>
        <FilterSection
          title="LEARNER TYPE"
          isExpanded={expansions.isLearnerTypeExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isLearnerTypeExpanded");
          }}
        >
          {audiences.filter((a) => !a.hideFromFilter).map((audience, index) => (
            <Checkbox
              key={index}
              className="mt-sm rounded-3"
              id={`audience-${audience.id}`}
              checked={selectedAudienceIds.includes(audience.id ? audience.id : "")}
              onChange={(event) => {
                const checked = event.target.checked;
                triggerOnChange({
                  ...value,
                  selectedAudienceIds: addOrRemove(
                    selectedAudienceIds,
                    audience.id ? audience.id : "",
                    checked
                  ),
                });
              }}
              labelProps={{
                text: audience.name ?? undefined,
              }}
            />
          ))}
        </FilterSection>
        <FilterSection
          title="TOPICS"
          isExpanded={expansions.isTopicsExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isTopicsExpanded");
          }}
        >
          {topicsBySpecialTopicOrder.map((topic, index) => {
            return (
              <div key={index} className="mt-sm">
                <Checkbox
                  id={topic.id}
                  checked={selectedTopicIds.includes(topic.id)}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    triggerOnChange({
                      ...value,
                      selectedTopicIds: addOrRemove(
                        selectedTopicIds,
                        topic.id,
                        checked
                      ),
                    });
                  }}
                  labelProps={{
                    text: topic.name ?? undefined,
                  }}
                />
              </div>
            );
          })}
        </FilterSection>

        <FilterSection
          title="STUDY FORMAT"
          isExpanded={expansions.isStudyFormatsExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isStudyFormatsExpanded");
          }}
        >
          {[
            StudyFormat.Online,
            StudyFormat.Hybrid,
            StudyFormat.Blended,
            StudyFormat.Campus,
          ].map((studyFormat, index) => (
            <Checkbox
              key={index}
              className="mt-sm rounded-3"
              id={`study-format-${studyFormat}`}
              checked={selectedStudyFormats.includes(studyFormat)}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                triggerOnChange({
                  ...value,
                  selectedStudyFormats: addOrRemove(
                    selectedStudyFormats,
                    studyFormat,
                    checked
                  ),
                });
              }}
              labelProps={{
                text: studyFormatToString(studyFormat) ?? undefined,
              }}
            />
          ))}
        </FilterSection>
        <FilterSection
          title="UNIVERSITY"
          isExpanded={expansions.isUniversitiesExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isUniversitiesExpanded");
          }}
        >
          {memberUniversities.map((memberUni, index) => (
            <Checkbox
              key={index}
              className="mt-sm rounded-3"
              id={`member-uni-${memberUni.id}`}
              checked={selectedUniversities.includes(memberUni)}
              onChange={(event) => {
                const checked = event.target.checked;
                triggerOnChange({
                  ...value,
                  selectedUniversityIds: addOrRemove(
                    selectedUniversityIds,
                    memberUni.id,
                    checked
                  ),
                });
              }}
              labelProps={{
                text: memberUni.name ?? undefined,
              }}
            />
          ))}
        </FilterSection>
        {tags !== undefined && tags.length > 0 ? (
          <FilterSection
            title="TAGS"
            isExpanded={expansions.isTagsExpanded}
            onExpandClicked={() => {
              triggerExpansionChange("isTagsExpanded");
            }}
          >
            {tags?.map((tag, index) => {
              return (
                <div key={index} className="mt-sm">
                  <Checkbox
                    id={tag.id}
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      triggerOnChange({
                        ...value,
                        selectedTagIds: addOrRemove(
                          selectedTagIds,
                          tag.id,
                          checked
                        ),
                      });
                    }}
                    labelProps={{
                      text: tag.name ?? undefined,
                    }}
                  />
                </div>
              );
            })}
          </FilterSection>
        ) : (
          <></>
        )}
        {paces !== undefined && paces.length > 0 ? (
          <FilterSection
            title="PACE"
            isExpanded={expansions.isPaceExpanded}
            onExpandClicked={() => {
              triggerExpansionChange("isPaceExpanded");
            }}
          >
            {paces?.map((pace, index) => {
              return (
                <div key={index} className="mt-sm">
                  <Checkbox
                    id={pace}
                    checked={selectedPaces.includes(pace)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      triggerOnChange({
                        ...value,
                        selectedPaces: addOrRemove(
                          selectedPaces,
                          pace,
                          checked
                        ),
                      });
                    }}
                    labelProps={{
                      text: pace ?? undefined,
                    }}
                  />
                </div>
              );
            })}
          </FilterSection>
        ) : (
          <></>
        )}
        {credits !== undefined && credits.length > 0 ? (
          <FilterSection
            title="CREDITS"
            isExpanded={expansions.isCreditsExpanded}
            onExpandClicked={() => {
              triggerExpansionChange("isCreditsExpanded");
            }}
          >
            {credits?.map((credit, index) => {
              return (
                <div key={index} className="mt-sm">
                  <Checkbox
                    id={credit}
                    checked={selectedCredits.includes(credit)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      triggerOnChange({
                        ...value,
                        selectedCredits: addOrRemove(
                          selectedCredits,
                          credit,
                          checked
                        ),
                      });
                    }}
                    labelProps={{
                      text: credit ?? undefined,
                    }}
                  />
                </div>
              );
            })}
          </FilterSection>
        ) : (
          <></>
        )}
        <FilterSection
          title="START DATE"
          isExpanded={expansions.isStudyPeriodExpanded}
          onExpandClicked={() => {
            triggerExpansionChange("isStudyPeriodExpanded");
          }}
        >
          <div className="flex flex-col pt-2">
            <label htmlFor="from" className="pb-1">
              From
            </label>
            <DatePicker
              id="from"
              onChange={(event) => setStartDate(event ? event.toString() : "")}
              selected={startDate ? new Date(startDate) : null}
              placeholderText="MM/DD/YYYY"
              customInput={
                <CustomInput
                  onClick={() => setStartDate("")}
                  value={
                    startDate
                      ? new Date(startDate).toLocaleDateString("en-GB")
                      : ""
                  }
                />
              }
              dateFormat={"MM/dd/yyyy"}
              maxDate={secondStartDate ? new Date(secondStartDate) : undefined}
            />
          </div>
          <div className="flex flex-col pt-2">
            <label htmlFor="from" className="pb-1">
              To
            </label>
            <DatePicker
              id="from"
              onChange={(event) =>
                setSecondStartDate(event ? event.toString() : "")
              }
              selected={secondStartDate ? new Date(secondStartDate) : null}
              placeholderText="MM/DD/YYYY"
              customInput={
                <CustomInput
                  onClick={() => setSecondStartDate("")}
                  value={
                    secondStartDate
                      ? new Date(secondStartDate).toLocaleDateString("en-GB")
                      : ""
                  }
                />
              }
              dateFormat={"MM/dd/yyyy"}
              minDate={startDate ? new Date(startDate) : undefined}
            />
          </div>
        </FilterSection>
      </div>
    </div>
  );
};
